import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { MessageCircle, X, Send } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 90vw;
  max-width: 800px;
  height: 80vh;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  z-index: 10000;
  transition: transform 0.3s ease;
`;

const Header = styled.div`
  background: #1877f2;
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const ConversationsList = styled.div`
  width: 300px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  background: #f8f9fa;
`;

const ConversationItem = styled.div<{ isActive: boolean; hasUnread: boolean }>`
  padding: 16px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  background: ${props => props.isActive ? '#e3f2fd' : 'white'};
  transition: background-color 0.2s;
  position: relative;

  &:hover {
    background: ${props => props.isActive ? '#e3f2fd' : '#f5f5f5'};
  }
`;

const ConversationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const ConversationName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const UnreadBadge = styled.div`
  background: #ef4444;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  padding: 0 6px;
`;

const LastMessage = styled.div`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
`;

const ChatHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
`;

const ChatTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MessageBubble = styled.div<{ isSender: boolean }>`
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 18px;
  background: ${props => props.isSender ? '#1877f2' : '#f0f2f5'};
  color: ${props => props.isSender ? 'white' : '#333'};
  align-self: ${props => props.isSender ? 'flex-end' : 'flex-start'};
  word-wrap: break-word;
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  text-align: ${props => props.isSender ? 'right' : 'left'};
`;

const InputArea = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const MessageInput = styled.textarea`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 12px 16px;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #1877f2;
  }
`;

const SendButton = styled.button`
  background: #1877f2;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #166fe5;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const NoConversations = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
  font-style: italic;
`;

const NoChatSelected = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #666;
  font-style: italic;
`;

// Add CSS animation for spinner
const GlobalStyles = styled.div`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Types
interface Conversation {
  _id: string;
  participants: string[];
  lastMessage: string;
  updatedAt: string;
  unreadCount: number;
  type: string;
}

interface Message {
  _id: string;
  content: string;
  sender: {
    id: string;
  };
  createdAt: string;
}

interface CompetitorConversationsProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: {
    id: string;
    name: string;
    role: string;
  };
}

const CompetitorConversations: React.FC<CompetitorConversationsProps> = ({
  isOpen,
  onClose,
  currentUser
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [competitorNames, setCompetitorNames] = useState<{ [id: string]: string }>({});
  const [isLoadingNames, setIsLoadingNames] = useState(false);
  const { token } = useAuth();
  const overlayRef = useRef<HTMLDivElement>(null);
  const notificationAudio = useRef<HTMLAudioElement | null>(null);
  const prevUnreadCounts = useRef<{ [convId: string]: number }>({});

  // Initialize audio notification
  useEffect(() => {
    notificationAudio.current = new window.Audio('/sounds/mixkit-positive-notification-951.wav');
  }, []);

  // Handle modal visibility
  useEffect(() => {
    if (isOpen && overlayRef.current) {
      requestAnimationFrame(() => {
        if (overlayRef.current) {
          overlayRef.current.classList.add('visible');
        }
      });
    } else if (overlayRef.current) {
      overlayRef.current.classList.remove('visible');
    }
  }, [isOpen]);

  // Fetch competitor names
  const fetchCompetitorNames = useCallback(async (conversations: Conversation[]) => {
    if (!token) return;
    
    setIsLoadingNames(true);
    const names: { [id: string]: string } = {};
    
    try {
      // First try to get all competitors at once (more efficient)
      const competitorsResponse = await api.get('/competitors/available?limit=100');
      if (competitorsResponse.data?.success) {
        const competitors = competitorsResponse.data.data || [];
        competitors.forEach((competitor: any) => {
          if (competitor.id || competitor._id) {
            const id = competitor.id || competitor._id;
            const fullName = competitor.firstName && competitor.lastName 
              ? `${competitor.firstName} ${competitor.lastName}`.trim()
              : competitor.firstName || competitor.lastName || competitor.name || 'Unknown User';
            names[id] = fullName;
          }
        });
      }
    } catch (error) {
      console.error('Error fetching competitors list:', error);
      
      // Fallback: fetch individual user names
      const promises = conversations.map(async (conv) => {
        const otherId = conv.participants.find(id => id !== currentUser.id);
        if (otherId && !competitorNames[otherId]) {
          try {
            const response = await api.get(`/users/${otherId}`);
            if (response.data?.success) {
              const userData = response.data.data;
              const fullName = userData.firstName && userData.lastName 
                ? `${userData.firstName} ${userData.lastName}`.trim()
                : userData.firstName || userData.lastName || userData.name || 'Unknown User';
              names[otherId] = fullName;
            }
          } catch (userError) {
            console.error(`Error fetching user ${otherId}:`, userError);
            names[otherId] = 'Unknown User';
          }
        }
      });
      
      await Promise.all(promises);
    }
    
    setCompetitorNames(prev => ({ ...prev, ...names }));
    setIsLoadingNames(false);
  }, [token, currentUser.id, competitorNames]);

  // Fetch conversations
  useEffect(() => {
    if (!isOpen || !token || !currentUser.id) return;

    const fetchConversations = async () => {
      try {
        const response = await api.get(`/messages/conversations?userId=${currentUser.id}`);
        if (response.data?.success) {
          const competitorConversations = response.data.data.filter((conv: Conversation) => 
            conv.type === 'competitor'
          );
          setConversations(competitorConversations);
          
          // Fetch competitor names for new conversations
          await fetchCompetitorNames(competitorConversations);
          
          // Check for new unread messages and play notification
          competitorConversations.forEach((conv: Conversation) => {
            const prevCount = prevUnreadCounts.current[conv._id] || 0;
            if (conv.unreadCount > prevCount && conv.unreadCount > 0) {
              if (notificationAudio.current) {
                notificationAudio.current.play().catch(console.error);
              }
            }
            prevUnreadCounts.current[conv._id] = conv.unreadCount;
          });
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
        setError('Failed to load conversations');
      }
    };

    fetchConversations();
    
    // Poll for new messages every 3 seconds
    const interval = setInterval(fetchConversations, 3000);
    return () => clearInterval(interval);
  }, [isOpen, token, currentUser.id, fetchCompetitorNames]);

  // Fetch messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation || !token) return;

    const fetchMessages = async () => {
      try {
        const response = await api.get(`/messages/${selectedConversation._id}`);
        if (response.data?.success) {
          setMessages(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        setError('Failed to load messages');
      }
    };

    fetchMessages();
  }, [selectedConversation, token]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (!selectedConversation || !token || !currentUser.id) return;

    const markAsRead = async () => {
      try {
        await api.post('/messages/mark-conversation-as-read', {
          conversationId: selectedConversation._id,
          userId: currentUser.id
        });
        
        // Update local state
        setConversations(prev => prev.map(conv => 
          conv._id === selectedConversation._id 
            ? { ...conv, unreadCount: 0 }
            : conv
        ));
        setSelectedConversation(prev => prev ? { ...prev, unreadCount: 0 } : null);
      } catch (error) {
        console.error('Error marking conversation as read:', error);
      }
    };

    markAsRead();
  }, [selectedConversation, token, currentUser.id]);

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation || !token) return;

    setIsSending(true);
    setError('');

    try {
      const otherParticipantId = selectedConversation.participants.find(id => id !== currentUser.id);
      if (!otherParticipantId) throw new Error('Invalid conversation');

      const messageData = {
        conversationId: selectedConversation._id,
        senderId: currentUser.id,
        receiverId: otherParticipantId,
        text: message.trim(),
        senderRole: currentUser.role,
        receiverRole: 'student',
        senderName: currentUser.name,
        receiverName: competitorNames[otherParticipantId] || 'Competitor'
      };

      const response = await api.post('/messages/send', messageData);

      if (response.data?.success) {
        setMessages(prev => [...prev, response.data.data]);
        setMessage('');
        
        // Update conversation list with new last message
        setConversations(prev => prev.map(conv => 
          conv._id === selectedConversation._id 
            ? { ...conv, lastMessage: message.trim(), updatedAt: new Date().toISOString() }
            : conv
        ));
      } else {
        throw new Error(response.data?.error || 'Failed to send message');
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getParticipantName = (conversation: Conversation) => {
    const otherId = conversation.participants.find(id => id !== currentUser.id);
    if (!otherId) return 'Unknown User';
    
    // Return the actual competitor name if available, otherwise fallback to generic
    return competitorNames[otherId] || `Competitor ${otherId.substring(0, 6)}`;
  };

  if (!isOpen) return null;

  return (
    <>
      <GlobalStyles />
      <ModalOverlay ref={overlayRef}>
        <ModalContent>
        <Header>
          <HeaderTitle>💬 Competitor Conversations</HeaderTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => {
                setIsLoadingNames(true);
                fetchCompetitorNames(conversations);
              }}
              disabled={isLoadingNames}
              style={{
                background: isLoadingNames ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: isLoadingNames ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: isLoadingNames ? 0.6 : 1
              }}
              title="Refresh competitor names"
            >
              {isLoadingNames ? '⏳ Loading...' : '🔄 Refresh Names'}
            </button>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </div>
        </Header>

        <Content>
          <ConversationsList>
            {conversations.length === 0 ? (
              <NoConversations>
                No conversations yet. Start messaging competitors to begin chatting!
              </NoConversations>
            ) : (
              <>
                {isLoadingNames && (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px'
                  }}>
                    Loading competitor names...
                  </div>
                )}
                {!isLoadingNames && conversations.length > 0 && Object.keys(competitorNames).length === 0 && (
                  <div style={{
                    padding: '16px',
                    textAlign: 'center',
                    color: '#ff9800',
                    fontSize: '12px',
                    background: '#fff3e0',
                    borderRadius: '8px',
                    margin: '0 16px 16px 16px'
                  }}>
                    ⚠️ Could not load competitor names. Click "Refresh Names" to retry.
                  </div>
                )}
                {conversations.map((conv) => {
                  const otherId = conv.participants.find(id => id !== currentUser.id);
                  const isNameLoaded = otherId && competitorNames[otherId];
                  
                  return (
                    <ConversationItem
                      key={conv._id}
                      isActive={selectedConversation?._id === conv._id}
                      hasUnread={conv.unreadCount > 0}
                      onClick={() => setSelectedConversation(conv)}
                    >
                      <ConversationHeader>
                        <ConversationName>
                          {isNameLoaded ? (
                            competitorNames[otherId!]
                          ) : (
                            <span style={{ 
                              color: '#999', 
                              fontStyle: 'italic',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <div style={{
                                width: '12px',
                                height: '12px',
                                border: '2px solid #ddd',
                                borderTop: '2px solid #1877f2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }} />
                              Loading...
                            </span>
                          )}
                        </ConversationName>
                        {conv.unreadCount > 0 && (
                          <UnreadBadge>
                            {conv.unreadCount}
                          </UnreadBadge>
                        )}
                      </ConversationHeader>
                      <LastMessage>
                        {conv.lastMessage || 'No messages yet'}
                      </LastMessage>
                    </ConversationItem>
                  );
                })}
              </>
            )}
          </ConversationsList>

          <ChatArea>
            {selectedConversation ? (
              <>
                <ChatHeader>
                  <ChatTitle>
                    Chat with {getParticipantName(selectedConversation)}
                  </ChatTitle>
                </ChatHeader>

                <MessagesContainer>
                  {messages.map((msg) => (
                    <MessageBubble
                      key={msg._id}
                      isSender={msg.sender.id === currentUser.id}
                    >
                      <div>{msg.content}</div>
                      <MessageTime isSender={msg.sender.id === currentUser.id}>
                        {new Date(msg.createdAt).toLocaleTimeString()}
                      </MessageTime>
                    </MessageBubble>
                  ))}
                </MessagesContainer>

                <InputArea>
                  <MessageInput
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message..."
                    rows={1}
                  />
                  <SendButton
                    onClick={handleSendMessage}
                    disabled={!message.trim() || isSending}
                  >
                    <Send size={16} />
                  </SendButton>
                </InputArea>
              </>
            ) : (
              <NoChatSelected>
                <MessageCircle size={48} />
                <div style={{ marginTop: '16px' }}>
                  Select a conversation to start chatting
                </div>
              </NoChatSelected>
            )}
          </ChatArea>
        </Content>

        {error && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            right: '20px',
            background: '#ef4444',
            color: 'white',
            padding: '12px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
      </ModalContent>
    </ModalOverlay>
      </>
  );
};

export default CompetitorConversations;
